// NPM Imports
import React, { Fragment, useState } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { Button } from "@revvsales/components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// CSS Imports
import signIn from "./signin.module.scss";

// Internal Imports
import NEW_ORG from "../../assets/new-org.png";
import { GlobalUtils } from "../../utils";

// Service Imports
import { triggerInviteSecretCode } from "../../AuthService/authService";

// Destructuring
const { writeDataToLocalStorage } = GlobalUtils;

// Declarations
const preferenceMapping = {
  p: "password",
  g: "google",
  s: "saml",
};

function OrgListing(props) {
  const history = useHistory();

  const {
    data: {
      domains,
      email,
      signUpInfo,
      invitedDomains,
      ociNewSignUp,
      selectedDomain,
    },
  } = props;

  // Local States
  const [state, setState] = useState({
    errMsg: "",
    selectedJoinBtnIndex: "",
    showJoinBtnLoader: false,
  });

  const _selectedDomain = (type, domainInfo, index) => {
    if (type === "invite") {
      // If SAML Login don't trigger secret code email
      if (domainInfo.preference === "s") {
        writeDataToLocalStorage("samlLoginOrgId", domainInfo.org_id);
        props.toggleView({
          currentView: "pendingInvite",
          selectedDomain: domainInfo.sub_domain,
          isVerified: domainInfo.is_verified,
        });
      } else {
        setState(prev => ({
          ...prev,
          showJoinBtnLoader: true,
          selectedJoinBtnIndex: index,
        }));

        const payload = {
          user_email: email,
          sub_domain: domainInfo.sub_domain,
          invite_by: domainInfo.invited_by_id,
        };

        triggerInviteSecretCode(payload)
          .then(response => {
            props.toggleView({
              currentView: "pendingInvite",
              selectedDomain: domainInfo.sub_domain,
              isVerified: domainInfo.is_verified,
            });
          })
          .catch(err => {
            console.error(
              `OrgListing => _selectedDomain => triggerInviteSecretCode => ${err}`,
            );
            setState(prev => ({
              ...prev,
              showJoinBtnLoader: true,
              selectedJoinBtnIndex: "",
              errMsg: "Something went wrong. Please try again!",
            }));
          });
      }
    } else {
      if (domainInfo.preference === "s") {
        writeDataToLocalStorage("samlLoginOrgId", domainInfo.org_id);
      }
      props.toggleView({
        currentView: preferenceMapping[domainInfo.preference],
        selectedDomain: domainInfo.sub_domain,
        isVerified: domainInfo.is_verified,
      });
    }
  };

  const _onGoBackClick = () => {
    props.toggleView({
      currentView: "verifyEmail",
      domains: [],
      email: "",
      isVerified: true,
      selectedDomain: "",
    });
  };

  const _allowNewOrgCreation = () => {
    history.push({
      pathname: "/signup/",
      search: `${window.location.search}`,
      state: {
        allowNewOrg: true,
        signUpInfo,
        email,
      },
    });
  };

  return (
    <Fragment>
      {/** HEADER SECTION */}
      <section>
        <div className={signIn.headerText}>Log into Revv</div>
        {state.errMsg && (
          <div className={signIn.errorMessage}>
            <span className={signIn.errorMessageIcon}>
              <FontAwesomeIcon icon={["far", "times-circle"]} />
            </span>
            {state.errMsg}
          </div>
        )}
      </section>

      {/** BODY SECTION */}

      {/** Existing Domain Listings */}
      {domains && domains.length > 0 && (
        <Fragment>
          <span className={signIn.fieldLabel}>
            which account would you like to login to?
          </span>
          <div className={`${signIn.domainWrapper}`}>
            {domains.map(domain => {
              let className = signIn.domainBox;
              if (!domain.active) {
                className += ` ${signIn.inactive}`;
              }
              if (selectedDomain === domain.sub_domain) {
                className += ` ${signIn.active}`;
              }
              return (
                <div
                  className={className}
                  key={domain.sub_domain}
                  id={domain.sub_domain}
                  onClick={() => _selectedDomain("existing", domain)}
                  onKeyPress={() => {}}
                  role="button"
                  tabIndex="0">
                  <span className={signIn.domainInitial}>
                    {domain.sub_domain.charAt(0)}
                  </span>
                  <span className={signIn.domainInfo}>
                    <span className={signIn.domainName}>
                      {domain.sub_domain}
                    </span>
                    <span className={signIn.domainMembersCount}>
                      {domain.user_count} members
                    </span>
                  </span>
                  <FontAwesomeIcon icon={["far", "arrow-right"]} />
                </div>
              );
            })}
          </div>
        </Fragment>
      )}

      {/** Invited Domain Listings */}
      {invitedDomains && invitedDomains.length > 0 && (
        <section>
          <div className={signIn.inviteSection}>
            <span className={signIn.fieldTitle}>Pending invitations</span>
            <span className={signIn.fieldLabel}>
              You are invited to these organisations
            </span>
          </div>
          <div className={`${signIn.domainWrapper}`}>
            {invitedDomains.map((domain, index) => {
              let className = signIn.inviteDomainBox;
              if (!domain.active) {
                className += ` ${signIn.inactive}`;
              }
              if (selectedDomain === domain.sub_domain) {
                className += ` ${signIn.active}`;
              }
              return (
                <div
                  className={className}
                  key={domain.sub_domain}
                  id={domain.sub_domain}
                  onClick={() => _selectedDomain("invite", domain, index)}
                  onKeyPress={() => {}}
                  role="button"
                  tabIndex="0">
                  <span className={signIn.inviteDomainInitial}>
                    {domain.sub_domain.charAt(0)}
                  </span>
                  <span className={signIn.domainInfo}>
                    <span className={signIn.domainName}>
                      {domain.sub_domain}
                    </span>
                    <span className={signIn.domainMembersCount}>
                      {domain.invited_by} invited you
                    </span>
                  </span>
                  <Button
                    type="three"
                    className={signIn.joinBtn}
                    isLoading={
                      state.showJoinBtnLoader &&
                      state.selectedJoinBtnIndex === index
                    }>
                    Join
                  </Button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/** Display create new org option */}
      {(!domains || (domains && !domains.length)) && !ociNewSignUp && (
        <div className={signIn.createNewOrgWrapper}>
          <div>OR</div>
          <div className={signIn.createNewOrgContainer}>
            <img
              src={NEW_ORG}
              alt="new-org"
              className={signIn.createNewOrgImage}
            />
            <span className={signIn.createNewOrgTxt}>
              Want to use Revv with a different team?
            </span>
            <Button
              type="three"
              className={signIn.createNewOrgBtn}
              onClick={() => _allowNewOrgCreation()}>
              Create new organisation
            </Button>
          </div>
        </div>
      )}

      {/** FOOTER SECTION */}
      <section>
        <Button
          data-test="signin-go-back"
          className={signIn.goBackBtn}
          onClick={() => _onGoBackClick()}>
          <FontAwesomeIcon
            icon={["far", "arrow-left"]}
            className={signIn.goBackBtnIcon}
          />
          Go back
        </Button>
      </section>
    </Fragment>
  );
}

OrgListing.defaultProps = {};

OrgListing.propTypes = {
  data: PropTypes.shape({
    email: PropTypes.string,
    domains: PropTypes.arrayOf(
      PropTypes.shape({
        sub_domain: PropTypes.string.isRequired,
      }),
    ),
    invitedDomains: PropTypes.arrayOf(
      PropTypes.shape({
        sub_domain: PropTypes.string.isRequired,
      }),
    ).isRequired,
    signUpInfo: PropTypes.shape({}),
    ociNewSignUp: PropTypes.bool,
    selectedDomain: PropTypes.string,
    selectedInvitedDomain: PropTypes.string,
    suggestion: PropTypes.shape({}),
  }).isRequired,
  toggleView: PropTypes.func.isRequired,
};

export default OrgListing;
